import React, {
  FC,
  ImgHTMLAttributes,
  useLayoutEffect,
  useCallback,
  useRef,
  useState,
} from "react";
import clsx from "classnames";
import { Skeleton } from "../Skeleton";
import { useStyles } from "./Image.style";
import { isTV } from "../../../utils/platform";

interface IImageProps {
  alt: string;
  src?: string;
  src2x?: string;
  src3x?: string;
  radius?: number | string;
  ignoreDecoding?: boolean;
}

const decodedCache = new Set<string>();

export const ImageComponent: FC<
  IImageProps & ImgHTMLAttributes<HTMLImageElement>
> = ({
  alt,
  width,
  height,
  radius,
  className,
  style,
  srcSet,
  src,
  src2x,
  src3x,
  ignoreDecoding,
  ...otherProps
}) => {
  const classes = useStyles();

  const ref = useRef<HTMLImageElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDecoded, setIsDecoded] = useState(
    () => !isTV || ignoreDecoding || (!!src && decodedCache.has(src)),
  );

  const onLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  const onError = useCallback(() => {
    if (ref.current) {
      if (src && ref.current.srcset !== src) {
        ref.current.srcset = src;
      } else {
        ref.current.style.opacity = "0";
      }
    }
  }, [src]);

  useLayoutEffect(() => {
    if (!isTV || !src || ignoreDecoding) return;

    if (decodedCache.has(src)) {
      setIsDecoded(true);
      return;
    }

    setIsDecoded(false);
    const img = ref.current!;
    let cancelled = false;

    const handler = () => {
      if (!cancelled) {
        decodedCache.add(src);
        setIsDecoded(true);
      }
    };
    img.decode().then(handler, handler);

    return () => {
      cancelled = true;
    };
  }, [src, ignoreDecoding]);

  const isHidden = (isLoading && !isTV) || !isDecoded;

  return (
    <>
      {/* Not TV */}
      {isLoading && !isTV && (
        <Skeleton
          {...otherProps}
          height={height}
          width={width}
          borderRadius={radius}
          className={clsx(classes.root, { isHide: !isLoading }, className)}
        />
      )}

      {/* is TV */}
      {!isDecoded && isTV && (
        <div
          className={clsx(classes.root, className)}
          style={{
            ...style,
            borderRadius: radius,
            width,
            height,
          }}
        />
      )}

      <img
        {...otherProps}
        className={clsx(classes.root, { isHide: isHidden }, className)}
        data-src={src2x ? `${src} 1x, ${src2x} 2x, ${src3x} 3x` : src}
        srcSet={src2x ? `${src} 1x, ${src2x} 2x, ${src3x} 3x` : src}
        // sizes="(max-width: 600px) 100px, (max-width: 1200px) 200px, 300px"
        style={{
          ...style,
          borderRadius: radius,
        }}
        src={src}
        ref={ref}
        alt={alt}
        width={width}
        height={height}
        onError={isTV ? undefined : onError}
        onLoad={isTV ? undefined : onLoad}
        decoding="async"
      />
    </>
  );
};

export const Image = React.memo(ImageComponent);
